import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';

const PREF_KEY = 'huscleos_scroll_top_enabled';

export const isScrollTopEnabled = () => {
  try { return localStorage.getItem(PREF_KEY) !== 'false'; } catch { return true; }
};

export const setScrollTopEnabled = (val) => {
  try { localStorage.setItem(PREF_KEY, val ? 'true' : 'false'); } catch { /* ignore */ }
  // Notify listeners (same-tab updates don't fire `storage` events)
  window.dispatchEvent(new CustomEvent('huscleos:scroll-top-pref'));
};

/**
 * Floating "back to top" button. Appears after scrolling 600px.
 * Respects a per-user preference (localStorage: huscleos_scroll_top_enabled).
 * Hidden on auth routes regardless of preference.
 */
export default function ScrollToTop() {
  const [visible, setVisible] = useState(false);
  const [enabled, setEnabled] = useState(isScrollTopEnabled());
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    const onPref = () => setEnabled(isScrollTopEnabled());
    window.addEventListener('storage', onPref);
    window.addEventListener('huscleos:scroll-top-pref', onPref);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('storage', onPref);
      window.removeEventListener('huscleos:scroll-top-pref', onPref);
    };
  }, []);

  // Hide on auth pages — form is short, button would crowd
  const hideOn = ['/staff/login', '/staff/forgot-password', '/staff/reset-password', '/staff/accept-invite'];
  if (hideOn.some((p) => pathname.startsWith(p))) return null;
  if (!enabled || !visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Scroll to top"
      data-testid="scroll-to-top"
      title="Scroll to top — disable this in Team Settings"
      className="fixed bottom-6 right-6 z-[70] w-12 h-12 flex items-center justify-center shadow-lg border transition-all hover:scale-110 hover:bg-[#FF2E00] hover:border-[#FF2E00] hover:text-white"
      style={{
        background: '#0F0F0F',
        borderColor: '#0F0F0F',
        color: '#FFFFFF',
        borderRadius: 0,
      }}
    >
      <ArrowUp size={18} />
    </button>
  );
}
